import {
  Close as CloseIcon,
  Login as LoginIcon,
  SupervisorAccount as SupervisorAccountIcon,
} from '@mui/icons-material'
import { LoadingButton } from '@mui/lab'
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  ListItemIcon,
  MenuItem,
  TextField,
} from '@mui/material'
import React, { useCallback, useState } from 'react'
import { useIntl } from 'react-intl'
import { useNavigate } from 'react-router'
import { toast } from 'react-toastify'
import { T } from '@/helpers/T'
import { useRoles } from '@/hooks/useRoles'
import { useAuthContext } from '@/providers/Global'

//	///////////////////////
//	MARK: TYPES
interface IProps {
  onClose(): void
}

//	///////////////////////
//	MARK: EXPORTS
const NavbarUserImpersonateInternal: React.FC<IProps> = ({ onClose }) => {
  const Auth = useAuthContext()
  const { authorities } = useRoles()
  const intl = useIntl()
  const navigate = useNavigate()

  const [open, setOpen] = useState(false)
  const [username, setUsername] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const isSuperAdmin = authorities.has('ROLE_SUPERADMIN')
  const isImpersonating = Boolean(Auth.active?.impersonation)

  const handleOpen = useCallback(() => {
    setUsername('')
    setError(null)
    setOpen(true)
    onClose()
  }, [onClose])

  const handleClose = useCallback(() => {
    if (loading) return
    setOpen(false)
  }, [loading])

  const handleSubmit = useCallback(async () => {
    const normalized = username.trim()

    if (!normalized) {
      setError(intl.formatMessage({ id: 'r.impersonation.username_required' }))
      return
    }

    setLoading(true)
    setError(null)

    try {
      await Auth.startImpersonationByUsername(normalized)

      toast.success(intl.formatMessage({ id: 'r.impersonation.success' }, { fullName: normalized }))

      setOpen(false)
      navigate('/')
    } catch (err) {
      const axiosError = err as {
        response?: { data?: { error?: string } }
        message?: string
      }
      const message =
        axiosError.response?.data?.error ??
        axiosError.message ??
        intl.formatMessage({ id: 'c.form.error_messages.500' })

      setError(message)
    } finally {
      setLoading(false)
    }
  }, [Auth, intl, navigate, username])

  if (!isSuperAdmin || isImpersonating) {
    return null
  }

  return (
    <>
      <MenuItem onClick={handleOpen} sx={{ py: 1 }}>
        <ListItemIcon>
          <SupervisorAccountIcon fontSize='small' color='error' />
        </ListItemIcon>
        <T id='r.impersonation.menu_label' />
      </MenuItem>

      <Dialog open={open} onClose={handleClose} fullWidth maxWidth='xs'>
        <DialogTitle>
          <T id='r.impersonation.dialog_title' />
        </DialogTitle>

        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            <T id='r.impersonation.dialog_help' />
          </DialogContentText>

          <TextField
            autoFocus
            fullWidth
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            label={<T id='r.impersonation.username_label' />}
            placeholder={intl.formatMessage({ id: 'r.impersonation.username_placeholder' })}
            disabled={loading}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !loading) {
                e.preventDefault()
                handleSubmit()
              }
            }}
          />

          {error && (
            <Alert severity='error' sx={{ mt: 2 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}
        </DialogContent>

        <DialogActions sx={{ justifyContent: 'space-between', px: 3, pb: 2 }}>
          <Button
            type='button'
            startIcon={<CloseIcon />}
            color='inherit'
            onClick={handleClose}
            disabled={loading}
          >
            <T id='lang.close' />
          </Button>

          <LoadingButton
            type='button'
            variant='contained'
            color='error'
            startIcon={<LoginIcon />}
            loading={loading}
            onClick={handleSubmit}
          >
            <T id='r.impersonation.start_button' />
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </>
  )
}

export const NavbarUserImpersonate = React.memo(
  NavbarUserImpersonateInternal,
) as typeof NavbarUserImpersonateInternal
